import {
  RevButton,
  RevCard,
  RevDialog,
  RevDialogFooter,
  RevInput,
  RevLink,
  RevPasswordStrengthMeter,
} from "@revmatics/catalyst";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ChangePasswordBodyType } from "../../../stores/types/profileTypes.tsx";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks.ts";
import { changePassword } from "../../../stores/thunks/profileThunk.tsx";
import { isPasswordValid } from "../../../helpers/functions/hasValidHelpers.ts";
import { passwordRequirements } from "../../../helpers/functions/getPasswordRequirements.ts";

export const SecuritySettingsPage = () => {
  const dispatch = useAppDispatch();
  const [isOpenChangePassword, setIsOpenChangePassword] = useState(false);
  const [isOpenDeleteAccount, setIsDeleteAccount] = useState(false);
  const isLoadingPasswordChange = useAppSelector(
    (state) => state.profile.isLoadingPasswordChange,
  );
  const methods = useForm<ChangePasswordBodyType>({
    mode: "onSubmit",
    defaultValues: {
      current_password: "",
      new_password: "",
    },
  });
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const onSubmit = (data: ChangePasswordBodyType) => {
    dispatch(
      changePassword({
        body: data,
        callback: () => setIsOpenChangePassword(false),
      }),
    );
  };
  return (
    <div className="max-w-[754px] mx-auto space-y-6">
      <h4 className="text-monochrome-900">Security</h4>
      <RevCard
        paddingTitle="px-6 py-4"
        title="Password"
        text="Manage your password"
      >
        <RevButton
          text="Change Password"
          variant="secondary"
          buttonSize="medium"
          handleClick={() => setIsOpenChangePassword(true)}
        />
      </RevCard>
      <RevCard paddingTitle="px-6 py-4" title="Permanently Remove">
        <RevButton
          text="Delete my user account "
          variant="secondary"
          buttonSize="medium"
          buttonColor="error"
          handleClick={() => setIsDeleteAccount(true)}
        />
      </RevCard>
      <RevDialog
        isOpen={isOpenDeleteAccount}
        title="Delete my user account"
        setIsOpen={() => setIsDeleteAccount(false)}
        btnList={[
          <RevButton
            text="Cancel"
            variant="secondary"
            handleClick={() => setIsDeleteAccount(false)}
            key="delete-user-account-cancel"
          />,
          <RevLink
            pathname="/contact-us"
            text="Contact Us"
            buttonVariant="primary"
            displayAs="button"
            buttonSize="medium"
            key="settidelete-user-account-contact-us"
          />,
        ]}
      >
        <div className="typography-body-sm text-monochrome-900">
          For deleting your account, please contact{" "}
          <a
            href="mailto:support@revmatics.ai"
            className="underline hover:no-underline"
          >
            support@revmatics.ai
          </a>{" "}
          so that a Revmatics specialist can help you.
        </div>
      </RevDialog>
      <RevDialog
        isOpen={isOpenChangePassword}
        title="Change password"
        setIsOpen={() => setIsOpenChangePassword(false)}
        className="lg:max-h-fit!"
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-3 w-full"
          noValidate
        >
          <RevInput
            name="current_password"
            label="Current password"
            type="password"
            size="md"
            required
            className="mb-0"
            register={register("current_password", {
              required: "Current password is required.",
            })}
            error={errors.current_password}
            enableClearButton={true}
            onClear={() =>
              setValue("current_password", "", { shouldValidate: false })
            }
          />
          <RevInput
            name="new_password"
            label="New password"
            type="password"
            size="md"
            required
            className="mb-0"
            register={register("new_password", {
              required: "New password is required.",
              validate: (value) => isPasswordValid(value),
            })}
            error={errors.new_password}
            enableClearButton={true}
            onClear={() =>
              setValue("new_password", "", { shouldValidate: false })
            }
          />
          <RevPasswordStrengthMeter
            password={watch("new_password", "")}
            requirements={passwordRequirements}
          />
          <RevDialogFooter
            list={[
              <RevButton
                text="Cancel"
                variant="secondary"
                handleClick={() => setIsOpenChangePassword(false)}
                key="change-password-cancel"
              />,
              <RevButton
                text="Save"
                type="submit"
                variant="primary"
                key="change-password-save"
                isLoading={isLoadingPasswordChange}
              />,
            ]}
          />
        </form>
      </RevDialog>
    </div>
  );
};
